// Rappel du premier message LinkedIn (décision du 26/10/2026) : après la demande de connexion, le message rédigé
// pour après l'acceptation est gardé sur l'affaire, en prochaine action datée. Rien n'est noté sans « Oui » :
// copier la note ne prouve pas l'envoi. L'outil ne voit pas l'acceptation (aucun accès à LinkedIn, règle 4).
import { useQueryClient } from "@tanstack/react-query";
import { useLocaleState, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Project } from "../types";
import { useLogActivity } from "./DealJournal";
import { useProjectDeal } from "./DealFollowUp";
import { nextActionText, useSaveNextAction } from "./NextActionEditor";
import { linkedInReminder, today } from "./nextAction";
import { formatDay } from "./projectUtils";

export const ConnectionSentPrompt = ({
  project,
  contact,
  message,
  onClose,
}: {
  project: Project;
  contact: { id: number; name: string };
  message: string;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const queryClient = useQueryClient();
  const { dealStages } = useConfigurationContext();
  const { deal, isPending } = useProjectDeal(project);
  const { log } = useLogActivity();
  const { save } = useSaveNextAction();
  const [busy, setBusy] = useState(false);
  if (isPending || deal?.archived_at) return null;
  const reminder = linkedInReminder(contact, message, today());
  // L'affaire n'a qu'une prochaine action : la remplacer se dit avant.
  const replaced =
    deal && (deal.next_action_type || deal.next_action || deal.next_action_date)
      ? nextActionText(deal, translate)
      : null;

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let dealId = deal?.id;
      if (!dealId) {
        const { data, error } = await getSupabaseClient().rpc(
          "follow_project",
          { project: project.id, first_stage: dealStages[0].value },
        );
        if (error) {
          notify(error.message, { type: "error" });
          return;
        }
        dealId = data;
        await queryClient.invalidateQueries({ queryKey: ["deals"] });
      }
      const activity = await log({
        deal_id: dealId!,
        contact_id: contact.id,
        kind: "linkedin",
        direction: "out",
        note: translate("resources.projects.assistant.connection_request"),
      });
      if (!activity) return;
      if (await save(dealId!, reminder, { quiet: true })) {
        notify("resources.projects.assistant.connection_reminder_saved", {
          type: "info",
          messageArgs: { date: formatDay(reminder.next_action_date, locale) },
        });
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <span>
        {translate("resources.projects.assistant.connection_sent", {
          name: contact.name,
          date: formatDay(reminder.next_action_date, locale),
        })}
      </span>
      {replaced && (
        <span className="text-xs text-muted-foreground">
          {translate("resources.projects.assistant.connection_replaces", {
            action: replaced,
          })}
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={confirm}>
          {translate(
            deal
              ? "resources.projects.assistant.connection_yes"
              : "resources.projects.assistant.connection_follow",
          )}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          {translate("resources.projects.contacts.sent_later")}
        </Button>
      </div>
    </div>
  );
};
